/**
 * Plan-gating — the single source of truth for "what can the user do".
 *
 * After the 2026-05 cornerstone refactor, gating is a pure function of:
 *   - funding_source  (the journey's money axis)
 *   - lifecycle_state (the journey's state axis)
 *   - heroes_enabled  (which heroes the journey is scoped to)
 *   - user role       (talent / family_master / family_member)
 *   - proSubscriptionActive (cross-journey override for Pro subscribers)
 *
 * Replaces the legacy three-layer cascade
 * (subscriptionMode + unlockMode + accessLevel) with a single function
 * driven by the canonical fields. Every consumer (journey-web,
 * business-web, citizen-mobile) imports from here.
 */

import type {
  AccessControlResult,
  AccessTier,
  FeatureType,
  FundingSource,
  JourneyForGating,
  JourneyTierBadge,
  LifecycleState,
  UserForGating,
} from './types';

export type * from './types';

// ═══════════════════════════════════════════════════════════════════════
// TIER DERIVATION
// ═══════════════════════════════════════════════════════════════════════

/**
 * Single source of truth for "what tier is this journey on".
 *   basic = limited hero exploration (free or invited-only)
 *   full  = all heroes unlocked (paid, pro, sponsored, family_shared)
 *
 * Mirrors the helper exported from @tonnie/database.
 */
export function tierOfFunding(source: FundingSource): AccessTier {
  return source === 'self_free' || source === 'invited_only' ? 'basic' : 'full';
}

/** Funding sources that grant full hero access. */
export const FULL_TIER_FUNDING_SOURCES: ReadonlySet<FundingSource> = new Set([
  'self_paid',
  'self_pro',
  'sponsored_b2b',
  'sponsored_gov',
  'family_shared',
]);

/** Funding sources that come from a B2B/B2G sponsorship. */
export const SPONSORED_FUNDING_SOURCES: ReadonlySet<FundingSource> = new Set([
  'sponsored_b2b',
  'sponsored_gov',
]);

/** Lifecycle states in which heroes / hero-dependent features are usable. */
export const HERO_USABLE_LIFECYCLES: ReadonlySet<LifecycleState> = new Set(['active']);

// ═══════════════════════════════════════════════════════════════════════
// FEATURE CATEGORIES
// ═══════════════════════════════════════════════════════════════════════

/** Features always accessible regardless of journey or tier. */
const BASE_FEATURES: ReadonlySet<FeatureType> = new Set([
  'home',
  'career',
  'cv_builder',
  'profile',
  'billing',
]);

/** Features that require an active journey AND are scoped by heroes_enabled. */
const HERO_FEATURES: ReadonlySet<FeatureType> = new Set([
  'checkup',
  'teleportal',
  'qultureup',
  'care',
  'passport',
]);

/**
 * Heroes that are FREE on every tier — full access, no upgrade, no cap.
 *
 * CheckUp is one, and always has been in substance: basic-tier journeys block
 * only Care, `LIMITED_FEATURE_CONFIG` has no CheckUp entry to cap, and company
 * invitations state outright that "CheckUp invites are ALWAYS free". What it
 * was NOT is a free feature in this function's eyes. Falling through to the
 * generic basic-tier branch returned `accessLevel: 'limited'` with
 * `reason: 'tier_required'`, and every surface that derives copy or a badge
 * from that verdict duly told free citizens to upgrade: a "Basic" badge beside
 * CheckUp in the sidebar, `plan.upgrade.checkup` offering "advanced CheckUp
 * features" that do not exist, and the Journey Builder's closing message
 * listing CheckUp among the heroes a plan unlocks. CheckUp was PAID BY
 * OMISSION — nobody decided it, the default decided it.
 *
 * Why this set and not `BASE_FEATURES` (H11): moving CheckUp there would skip
 * the journey checks entirely and report full access with NO journey at all,
 * to a user who has nothing to take a CheckUp against. The two checks that
 * matter — a journey exists, and it is active — stay; only the tier verdict is
 * short-circuited. See `checkAccess` step 2d.
 */
const FREE_HERO_FEATURES: ReadonlySet<FeatureType> = new Set(['checkup']);

/**
 * Whether a hero is free on every tier, so a caller can render (or not render)
 * an upgrade affordance without re-deriving the rule.
 */
export function isFreeHeroFeature(feature: FeatureType): boolean {
  return FREE_HERO_FEATURES.has(feature);
}

// ═══════════════════════════════════════════════════════════════════════
// CORE GATING
// ═══════════════════════════════════════════════════════════════════════

/**
 * The one gating function. Pure — no side effects, no IO.
 *
 * Decision tree:
 *   1. No user → block (tier_required).
 *   2. Base features (home/career/cv_builder/profile/billing) → always allowed.
 *   3. Hero features:
 *      a. No journey                         → block (no_journey).
 *      b. Lifecycle ≠ active                 → block (journey_not_active).
 *      c. Hero NOT in journey.heroes_enabled
 *         AND user is not on a full tier     → block (hero_not_enabled).
 *      d. Hero is FREE on every tier         → allow full, whatever the tier.
 *      e. Tier is full                       → allow full.
 *      f. Tier is basic                      → per-feature rules:
 *         - care → block (tier_required, upgrade)
 *         - others → allow LIMITED (caps in LIMITED_FEATURE_CONFIG).
 *   4. Special features:
 *      - journey_builder   → all users; full tier or pro = unlimited.
 *      - copilot           → quota-limited for non-pro.
 *      - family_program    → requires full tier or sponsored or family_shared.
 *
 * (The tree previously opened with "family-member role → full role lookup".
 * There is no such branch and there has not been one since the cornerstone
 * refactor; role is carried on `UserForGating` and consumed by callers. The
 * stale step also made every step below it off by one.)
 */
export function checkAccess(
  user: UserForGating | null,
  feature: FeatureType,
  journey?: JourneyForGating | null
): AccessControlResult {
  if (!user) {
    return { canAccess: false, accessLevel: 'none', reason: 'tier_required' };
  }

  // 1. Base features always pass.
  if (BASE_FEATURES.has(feature)) {
    return { canAccess: true, accessLevel: 'full' };
  }

  // 2. Hero features need an active journey.
  if (HERO_FEATURES.has(feature)) {
    if (!journey) {
      return { canAccess: false, accessLevel: 'none', reason: 'no_journey' };
    }
    if (!HERO_USABLE_LIFECYCLES.has(journey.lifecycleState)) {
      return { canAccess: false, accessLevel: 'none', reason: 'journey_not_active' };
    }

    const tier = tierOfFunding(journey.fundingSource);
    const isFullTier = tier === 'full' || user.proSubscriptionActive === true;

    // Scope check — but Pro/full bypasses it (see W6 lessons in the
    // cornerstone review: narrowing heroes_enabled at create time
    // shouldn't permanently lock a paid user out).
    const scope = journey.heroesEnabled;
    const inScope = !scope || scope.includes(feature);
    if (!inScope && !isFullTier) {
      return { canAccess: false, accessLevel: 'none', reason: 'hero_not_enabled' };
    }

    // F36 — free heroes are resolved BEFORE the tier verdict, and only here.
    //
    // Deliberately placed after the journey and scope checks and before the
    // tier one. A free hero is free on every tier; it is not free without a
    // journey, and it is not free outside the journey's scope. Moving CheckUp
    // into BASE_FEATURES would have answered "full access" to a user with no
    // journey at all (H11) — the opposite failure, and a harder one to see.
    //
    // No `reason` and no `upgradeRequired`: this is a plain yes, and every
    // badge and upgrade string downstream is derived from those two fields.
    if (FREE_HERO_FEATURES.has(feature)) {
      return { canAccess: true, accessLevel: 'full' };
    }

    if (isFullTier) {
      return { canAccess: true, accessLevel: 'full' };
    }

    // Basic-tier feature rules.
    if (feature === 'care') {
      return {
        canAccess: false,
        accessLevel: 'none',
        reason: 'tier_required',
        upgradeRequired: true,
      };
    }
    return { canAccess: true, accessLevel: 'limited', reason: 'tier_required' };
  }

  // 3. Special features.
  if (feature === 'journey_builder') {
    const isFullTier =
      user.proSubscriptionActive === true ||
      (journey ? tierOfFunding(journey.fundingSource) === 'full' : false);
    return {
      canAccess: true,
      accessLevel: isFullTier ? 'full' : 'limited',
    };
  }

  if (feature === 'copilot') {
    return {
      canAccess: true,
      accessLevel: user.proSubscriptionActive ? 'full' : 'limited',
    };
  }

  if (feature === 'family_program') {
    const isFullTier =
      user.proSubscriptionActive === true ||
      (journey ? tierOfFunding(journey.fundingSource) === 'full' : false);
    if (isFullTier) {
      return { canAccess: true, accessLevel: 'full' };
    }
    return {
      canAccess: false,
      accessLevel: 'none',
      reason: 'tier_required',
      upgradeRequired: true,
    };
  }

  return { canAccess: false, accessLevel: 'none', reason: 'tier_required' };
}

/** Boolean shortcut. */
export function canAccessFeature(
  user: UserForGating | null,
  feature: FeatureType,
  journey?: JourneyForGating | null
): boolean {
  return checkAccess(user, feature, journey).canAccess;
}

/** Returns the access level (full / limited / none). */
export function getFeatureAccessLevel(
  user: UserForGating | null,
  feature: FeatureType,
  journey?: JourneyForGating | null
): 'full' | 'limited' | 'none' {
  return checkAccess(user, feature, journey).accessLevel;
}

/** Whether the UI should show an upgrade CTA. */
export function shouldShowUpgradeCTA(
  user: UserForGating | null,
  feature: FeatureType,
  journey?: JourneyForGating | null
): boolean {
  return checkAccess(user, feature, journey).upgradeRequired === true;
}

// ═══════════════════════════════════════════════════════════════════════
// FEATURE LIMITS — what "limited" tier means per feature
// ═══════════════════════════════════════════════════════════════════════

export interface QultureUpLimits {
  maxWorlds: number;
  maxMissionsPerWorld: number;
}

export interface PassportLimits {
  canViewAnalytics: boolean;
}

export interface JourneyBuilderLimits {
  maxJourneys: number;
}

/**
 * TelePortal has no entry: what a free journey may do there is the server's
 * per-task gate (`lockReason`), which both portals read (D6, 21 Oct 2026). A
 * plan-wide "no documents on Basic" rule here disagreed with it.
 */
export interface FeatureLimits {
  qultureup: QultureUpLimits;
  passport: PassportLimits;
  journey_builder: JourneyBuilderLimits;
}

export const LIMITED_FEATURE_CONFIG: FeatureLimits = {
  qultureup: { maxWorlds: 1, maxMissionsPerWorld: 3 },
  passport: { canViewAnalytics: false },
  // Free users can build unlimited journeys; each starts as 'basic'.
  journey_builder: { maxJourneys: Infinity },
};

export const FULL_FEATURE_CONFIG: FeatureLimits = {
  qultureup: { maxWorlds: Infinity, maxMissionsPerWorld: Infinity },
  passport: { canViewAnalytics: true },
  journey_builder: { maxJourneys: Infinity },
};

export function getFeatureLimits<K extends keyof FeatureLimits>(
  user: UserForGating | null,
  feature: K,
  journey?: JourneyForGating | null
): FeatureLimits[K] {
  const result = checkAccess(user, feature as FeatureType, journey);
  return result.accessLevel === 'full'
    ? FULL_FEATURE_CONFIG[feature]
    : LIMITED_FEATURE_CONFIG[feature];
}

// ═══════════════════════════════════════════════════════════════════════
// JOURNEY CARD BADGES (F47)
// ═══════════════════════════════════════════════════════════════════════

/**
 * The access badges for one journey card — tier, funding kind and whether the
 * company name is an invitation attribution. Computed by the API for the
 * journey list and rendered by the clients as-is (root CLAUDE.md rule 9).
 *
 * Exhaustive over `FundingSource`: a new source fails the build here instead of
 * rendering as whatever a client's fallback branch happened to say.
 */
export function journeyTierBadge(fundingSource: FundingSource): JourneyTierBadge {
  const tier = tierOfFunding(fundingSource);
  switch (fundingSource) {
    case 'self_free':
      return { tier, funding: 'free', inviterAttributed: false };
    case 'self_paid':
      return { tier, funding: 'self_paid', inviterAttributed: false };
    case 'self_pro':
      return { tier, funding: 'pro', inviterAttributed: false };
    case 'invited_only':
      return { tier, funding: 'invited', inviterAttributed: true };
    case 'sponsored_b2b':
      return { tier, funding: 'sponsored_company', inviterAttributed: true };
    case 'sponsored_gov':
      return { tier, funding: 'sponsored_government', inviterAttributed: true };
    case 'family_shared':
      return { tier, funding: 'family', inviterAttributed: false };
    default: {
      const exhaustive: never = fundingSource;
      throw new Error(`journeyTierBadge: unhandled funding source ${String(exhaustive)}`);
    }
  }
}
